import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import Api from "../../../../shared/api/api";
import BookDescription from "./BookDescription";
import { retailerKey, trackBookView, trackEvent } from "../../helpers/analytics";
import "../../styles/BookDetail.css";

export default function BookDetail() {
  const { title } = useParams();
  const [book, setBook] = useState(null);
  const [loadingFormat, setLoadingFormat] = useState(null);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const discountCode = params.get("code") || "";

  useEffect(() => {
    if (book?.title === title) trackBookView(book.title, location.key);
  }, [book, title, location.key]);

  function formatDate(dateString) {
    if (!dateString) return "";
    const safe = dateString.replace(/-/g, "/");
    const d = new Date(safe);
    if (isNaN(d)) return dateString;

    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }


  useEffect(() => {
    const fetchBook = async () => {
      try {
        const data = await Api.getByTitleOrUsername("books", title);
        setBook(data.book);
      } catch (error) {
        console.error("Error fetching book details:", error);
      }
    };
    fetchBook();
  }, [title]);

  // ⭐ Add-to-cart for books
  const handleAddToCart = async (format, price) => {
    try {
      const res = await Api.addBookToCart({
        title: book.title,
        format,
        price,
        image_url: book.image
      });
      window.dispatchEvent(new Event("cart-updated"));
    } catch (err) {
      console.error("Add to cart failed:", err);
      alert("Could not add to cart.");
    }
  };


  if (!book) return <div className="loading">Loading...</div>;

  return (
    <div className="book-detail-container">
      <div className="book-content">
        <img src={book.image} alt={book.title} className="book-image" />

        <div className="book-details">
          <h1 className="book-title">{book.title}</h1>

          {(book.series || book.book_series) && (
            <p className="book-series">
              {book.series || book.book_series}
              {book.book_number ? ` — Book ${book.book_number}` : ""}
            </p>
          )}

          <BookDescription description={book.description} />

          <div className="book-buy-section">
          {book.title === "Dawn of Prophecy" && <p><Link to="/contact#newsletter">Get the full ebook free with newsletter signup</Link>, or buy it for $0.99.</p>}

          {/* ⭐ If direct purchase link exists → show Add to Cart buttons */}
          {book.direct_purchase_url ? (
            <>
              {book.ebook_price > 0 && (
                <button
                  className="shop-card-button"
                  onClick={() =>
                    handleAddToCart("ebook", book.ebook_price)
                  }
                  disabled={loadingFormat === "ebook"}
                >
                  Add eBook to Cart - ${book.ebook_price}
                </button>
              )}

              {book.paperback_price > 0 && (
                <button
                  className="shop-card-button"
                  onClick={() =>
                    handleAddToCart("paperback", book.paperback_price)
                  }
                  disabled={loadingFormat === "paperback"}
                >
                  Add Paperback to Cart - ${book.paperback_price}
                </button>
              )}

              {book.hardcover_price > 0 && (
                <button
                  className="shop-card-button"
                  onClick={() =>
                    handleAddToCart("hardcover", book.hardcover_price)
                  }
                  disabled={loadingFormat === "hardcover"}
                >
                  Add Hardcover to Cart - ${book.hardcover_price}
                </button>
              )}

              {book.audiobook_price > 0 && (
                <button
                  className="shop-card-button"
                  onClick={() =>
                    handleAddToCart("audiobook", book.audiobook_price)
                  }
                  disabled={loadingFormat === "audiobook"}
                >
                  Add Audiobook to Cart - ${book.audiobook_price}
                </button>
              )}
            </>
          ) : (
            <>
              {/* 🌎 Fallback External Vendor Buttons (when direct_purchase_url is null) */}
              {book.ebook && (
                <a
                  href={book.ebook}
                  onClick={() => trackEvent("purchase_click", { book_title: book.title, destination_retailer: retailerKey(book.ebook) })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shop-card-button fallback-button"
                >
                  Purchase eBook from outside vendor
                </a>
              )}

              {book.paperback && (
                <a
                  href={book.paperback}
                  onClick={() => trackEvent("purchase_click", { book_title: book.title, destination_retailer: retailerKey(book.paperback) })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shop-card-button fallback-button"
                >
                  Purchase Paperback from outside vendor
                </a>
              )}

              {book.hardcover && (
                <a
                  href={book.hardcover}
                  onClick={() => trackEvent("purchase_click", { book_title: book.title, destination_retailer: retailerKey(book.hardcover) })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shop-card-button fallback-button"
                >
                  Purchase Hardcover from outside vendor
                </a>
              )}
            </>
          )}

          {book.audiobook_link && (
            <a href={book.audiobook_link} target="_blank" rel="noopener noreferrer"
              className="shop-card-button fallback-button"
              onClick={() => trackEvent("audiobook_click", { book_title: book.title, destination_retailer: retailerKey(book.audiobook_link) })}>
              Audiobook
            </a>
          )}
        </div>

        </div>
      </div>

      <div className="book-extra">
        {book.release_date && (
          <p>
            <strong>Released:</strong> {formatDate(book.release_date)}
          </p>
        )}
        {book.publisher && (
          <p>
            <strong>Publisher:</strong> {book.publisher}
          </p>
        )}
      </div>
    </div>
  );
}
